import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../components/DataTable';
import { getAllCareers, updateCareerStatus, deleteCareer, sendScheduleEmail } from '../redux/slice/career.slice';
import { getAllProjects } from '../redux/slice/project.slice';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { BASE_URL } from '../utils/BASE_URL';
import {
    Trash2, ChevronDown, Download, FileText, Search, Filter,
    CheckCircle, Clock, AlertCircle, Check, Briefcase, UserCheck, Copy, Mail,
    Cross, X, Calendar
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { motion, AnimatePresence } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ExportDateRangeModal from '../components/ExportDateRangeModal';
import ViewCareerModal from '../components/ViewCareerModal';

const Careers = () => {
    const dispatch = useDispatch();
    const { applications, count, loading, error } = useSelector((state) => state.career);
    const { projects } = useSelector((state) => state.project);

    // Local State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Modal States
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [careerToDelete, setCareerToDelete] = useState(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [interviewModalOpen, setInterviewModalOpen] = useState(false);

    // Export States
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportType, setExportType] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowExportDropdown(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        dispatch(getAllCareers());
        dispatch(getAllProjects());
    }, [dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Statistics Calculation
    const stats = {
        total: applications?.length || 0,
        pending: applications?.filter(a => a.status === 'Pending')?.length || 0,
        interviewing: applications?.filter(a => a.status === 'Interviewing')?.length || 0,
        hired: applications?.filter(a => a.status === 'Hired')?.length || 0
    };

    // Filter Logic
    const filteredApplications = applications?.filter(app => {
        const matchesSearch =
            (app.fullname?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (app.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (app.position?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (app.phone || '').includes(searchTerm);

        const matchesStatus = statusFilter === 'All' || app.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const paginatedApplications = filteredApplications?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        setCareerToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleView = (id) => {
        const application = applications.find(app => app._id === id);
        if (application) {
            setSelectedApplication(application);
            setViewModalOpen(true);
        }
    };

    const confirmDelete = () => {
        if (careerToDelete) {
            dispatch(deleteCareer(careerToDelete));
            setDeleteModalOpen(false);
            setCareerToDelete(null);
        }
    };


    const handleStatusChange = (id, newStatus) => {
        const formData = new FormData();
        formData.append('career_id', id);
        formData.append('status', newStatus);
        dispatch(updateCareerStatus({ id, formData }));
    };

    const handleExportClick = (type) => {
        setExportType(type);
        setExportModalOpen(true);
        setShowExportDropdown(false);
    };

    const handleExport = (startDate, endDate) => {
        const filterByDate = (start, end) => {
            const sDate = new Date(start); sDate.setHours(0, 0, 0, 0);
            const eDate = new Date(end); eDate.setHours(23, 59, 59, 999);
            return applications.filter(app => {
                const appDate = new Date(app.created_at || app.createdAt);
                return appDate >= sDate && appDate <= eDate;
            });
        };

        const dataToExport = filterByDate(startDate, endDate);

        if (dataToExport.length === 0) {
            alert('No data found for the selected date range.');
            return;
        }

        if (exportType === 'Excel') {
            exportToExcel(dataToExport, startDate, endDate);
        } else {
            exportToPDF(dataToExport, startDate, endDate);
        }
    };

    const exportToExcel = (data, startDate, endDate) => {
        const dataToExport = data.map(app => ({
            'Full Name': app.fullname,
            'Email': app.email,
            'Phone': app.phone,
            'Position': app.position,
            'Experience': app.experience,
            'Cover Letter': app.cover_latter || 'N/A',
            'Status': app.status,
            'Date': new Date(app.created_at || app.createdAt).toLocaleString()
        }));
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Careers");
        XLSX.writeFile(workbook, `careers_${startDate}_to_${endDate}.xlsx`);
    };

    const exportToPDF = (data, startDate, endDate) => {
        const doc = new jsPDF();
        doc.text("Careers Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Range: ${startDate} to ${endDate}`, 14, 22);

        const tableColumn = ["Name", "Email", "Phone", "Position", "Status", "Date"];
        const tableRows = data.map(app => [
            app.fullname,
            app.email,
            app.phone,
            app.position,
            app.status,
            new Date(app.created_at || app.createdAt).toLocaleDateString()
        ]);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 35 });
        doc.save(`careers_${startDate}_to_${endDate}.pdf`);
    };

    const statusColors = {
        'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
        // 'Reviewed': 'bg-blue-100 text-blue-800 border-blue-200',
        'Interviewing': 'bg-purple-100 text-purple-800 border-purple-200',
        'Rejected': 'bg-red-100 text-red-800 border-red-200',
        'Hired': 'bg-green-100 text-green-800 border-green-200',
    };

    const columns = [
        {
            header: 'Candidate',
            accessor: 'fullname',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">{row.fullname}</span>
                    {/* <a href={`mailto:${row.email}`} className="text-xs text-gray-500 hover:text-blue-600 truncate max-w-[150px]">{row.email}</a> */}
                </div>
            )
        },
        {
            header: 'Phone',
            accessor: 'phone',
            render: (row) => (
                <div className="flex items-center gap-2 group">
                    <a href={`tel:${row.phone}`} className="text-sm text-gray-600 hover:text-blue-600 font-medium">
                        {row.phone}
                    </a>
                    <CopyButton text={row.phone} />
                </div>
            )
        },
        { header: 'Position', accessor: 'position', className: "md:table-cell font-medium text-gray-700" },
        { header: 'Experience', accessor: 'experience', className: "lg:table-cell text-gray-600" },
        {
            header: 'Status',
            accessor: 'status',
            render: (row) => (
                <StatusSelect
                    currentStatus={row.status}
                    onStatusChange={(newStatus) => handleStatusChange(row._id, newStatus)}
                    statusColors={statusColors}
                />
            )
        },
        {
            header: 'Date',
            accessor: 'created_at',
            className: "sm:table-cell",
            render: (row) => (
                <div className="text-sm text-gray-500 whitespace-nowrap">
                    {new Date(row.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
            )
        }
    ];

    if (loading) return (
        <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );



    return (
        <div className="max-w-[95%] mx-auto py-8">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-serif font-bold text-gray-900">Careers</h2>
                    <p className="text-gray-500 mt-1">Manage job applications and candidates</p>
                </div>

                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setShowExportDropdown(!showExportDropdown)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all shadow-md hover:shadow-lg font-medium text-sm"
                    >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Data</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showExportDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showExportDropdown && (
                        <div className="absolute left-0 lg:left-auto lg:right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20 animate-fade-in-up">
                            <button
                                onClick={() => handleExportClick('Excel')}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors"
                            >
                                <div className="p-1.5 bg-green-50 rounded text-green-600">
                                    <FileText className="w-4 h-4" />
                                </div>
                                <span className="font-medium text-sm">Export to Excel</span>
                            </button>
                            <button
                                onClick={() => handleExportClick('PDF')}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors border-t border-gray-50"
                            >
                                <div className="p-1.5 bg-red-50 rounded text-red-600">
                                    <FileText className="w-4 h-4" />
                                </div>
                                <span className="font-medium text-sm">Export to PDF</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard
                    title="Total Applications"
                    value={stats.total}
                    icon={Briefcase}
                    color="bg-blue-50 text-blue-600"
                />
                <StatCard
                    title="Pending Review"
                    value={stats.pending}
                    icon={Clock}
                    color="bg-yellow-50 text-yellow-600"
                />
                <StatCard
                    title="Interviewing"
                    value={stats.interviewing}
                    icon={UserCheck}
                    color="bg-purple-50 text-purple-600"
                />
                <StatCard
                    title="Hired"
                    value={stats.hired}
                    icon={CheckCircle}
                    color="bg-green-50 text-green-600"
                />
            </div>

            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col lg:flex-row gap-4 justify-between items-center">
                <div className="relative w-full lg:max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search candidates, positions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-all"
                    />
                </div>

                <div className="relative w-full lg:w-auto" ref={statusDropdownRef}>
                    <button
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        className="w-full lg:w-48 flex items-center justify-between px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors shadow-sm"
                    >
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span>{statusFilter === 'All' ? 'All Status' : statusFilter}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showStatusDropdown && (
                        <div className="absolute right-0 top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20 animate-fade-in-up">
                            {['All', 'Pending', 'Interviewing', 'Rejected', 'Hired'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => {
                                        setStatusFilter(status);
                                        setShowStatusDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between text-sm transition-colors ${statusFilter === status ? 'text-blue-600 bg-blue-50/50 font-medium' : 'text-gray-700'
                                        }`}
                                >
                                    <span>{status === 'All' ? 'All Status' : status}</span>
                                    {statusFilter === status && <Check className="w-4 h-4" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Data Table */}
            <DataTable
                columns={columns}
                data={paginatedApplications}
                onEdit={() => { }} // No edit modal needed for now
                onDelete={(id) => handleDelete(id)}
                onView={(id) => handleView(id)}
                showEdit={false}
                showView={true}
                error={error}
                pagination={true}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredApplications?.length || 0}
                onPageChange={handlePageChange}
                renderCustomAction={(row) => (
                    <button
                        onClick={() => {
                            if (['Pending', 'Interviewing'].includes(row.status)) {
                                setSelectedApplication(row);
                                setInterviewModalOpen(true);
                            }
                        }}
                        disabled={!['Pending', 'Interviewing'].includes(row.status)}
                        className={`p-2 rounded-lg transition-all ${['Pending', 'Interviewing'].includes(row.status)
                            ? 'text-blue-600 hover:bg-blue-50 hover:shadow-sm'
                            : 'text-gray-300 cursor-not-allowed'
                            }`}
                        title={['Pending', 'Interviewing'].includes(row.status) ? "Schedule Interview" : "Application Processed"}
                    >
                        <Mail className="w-4 h-4" />
                    </button>
                )}
            />

            <ViewCareerModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                application={selectedApplication}
            />

            <ExportDateRangeModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExport}
                type={exportType}
            />

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Application"
                message="Are you sure you want to delete this application? This action cannot be undone."
            />

            <ScheduleInterviewModal
                isOpen={interviewModalOpen}
                onClose={() => setInterviewModalOpen(false)}
                application={selectedApplication}
                projects={projects}
            />
        </div>
    );
};

const ScheduleInterviewModal = ({ isOpen, onClose, application, projects }) => {
    const dispatch = useDispatch();

    const validationSchema = Yup.object().shape({
        date: Yup.string().required('Interview date is required'),
        time: Yup.string().required('Interview time is required'),
        selectedAddress: Yup.string().required('Please select an office address'),
    });

    const formik = useFormik({
        initialValues: {
            date: '',
            time: '',
            selectedAddress: '',
        },
        validationSchema,
        onSubmit: (values) => {
            const [year, month, day] = values.date.split('-');
            const formattedDate = `${day}/${month}/${year}`;

            const formData = new FormData();
            formData.append('carrier_id', application._id);
            formData.append('date', formattedDate);
            formData.append('time', values.time);
            formData.append('address', values.selectedAddress);

            dispatch(sendScheduleEmail(formData)).then((res) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    handleClose();
                }
            });
        },
    });

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-secondary/10 rounded-lg text-secondary">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-800">Schedule Interview</h3>
                                <p className="text-xs text-gray-500">Invite {application?.fullname} for an interview</p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={formik.handleSubmit}>
                        <div className="p-4 md:p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700">Interview Date</label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formik.values.date}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-shadow text-sm ${formik.touched.date && formik.errors.date ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                    />
                                    {formik.touched.date && formik.errors.date && (
                                        <p className="text-xs text-red-500">{formik.errors.date}</p>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700">Interview Time</label>
                                    <input
                                        type="time"
                                        name="time"
                                        value={formik.values.time}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent outline-none transition-shadow text-sm ${formik.touched.time && formik.errors.time ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                    />
                                    {formik.touched.time && formik.errors.time && (
                                        <p className="text-xs text-red-500">{formik.errors.time}</p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Select Office Address</label>
                                <div className="max-h-[220px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                                    <label className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${formik.values.selectedAddress === 'VALLETTA VISTA, Palanpur, Nr. Jalaram School, Surat - 395009.' ? 'border-secondary bg-secondary/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                                        <input
                                            type="radio"
                                            name="selectedAddress"
                                            value="VALLETTA VISTA, Palanpur, Nr. Jalaram School, Surat - 395009."
                                            checked={formik.values.selectedAddress === 'VALLETTA VISTA, Palanpur, Nr. Jalaram School, Surat - 395009.'}
                                            onChange={formik.handleChange}
                                            className="mt-1 accent-secondary"
                                        />
                                        <div className="text-sm">
                                            <p className="font-bold text-gray-900">Main Office</p>
                                            <p className="text-gray-500 text-xs">VALLETTA VISTA, Palanpur, Nr. Jalaram School, Surat - 395009.</p>
                                        </div>
                                    </label>

                                    {projects?.map((project) => (
                                        <label key={project._id} className={`flex items-start gap-3 p-3 border rounded-xl cursor-pointer transition-all ${formik.values.selectedAddress === project?.officeAddress ? 'border-secondary bg-secondary/5' : 'border-gray-100 hover:bg-gray-50'}`}>
                                            <input
                                                type="radio"
                                                name="selectedAddress"
                                                value={project?.officeAddress}
                                                checked={formik.values.selectedAddress === project?.officeAddress}
                                                onChange={formik.handleChange}
                                                className="mt-1 accent-secondary"
                                            />
                                            <div className="text-sm">
                                                <p className="font-bold text-gray-900">{project.title} Site</p>
                                                <p className="text-gray-500 text-xs">{project.officeAddress || 'Address not listed'}</p>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                                {formik.touched.selectedAddress && formik.errors.selectedAddress && (
                                    <p className="text-xs text-red-500">{formik.errors.selectedAddress}</p>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 md:p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={formik.isSubmitting}
                                className="px-6 py-2 bg-secondary text-white font-medium rounded-lg hover:bg-secondary/90 transition-colors shadow-lg shadow-secondary/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Mail className="w-4 h-4" />
                                {formik.isSubmitting ? 'Sending...' : 'Send Invite'}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>,
        document.body
    );
};


const StatusSelect = ({ currentStatus, onStatusChange, statusColors }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                buttonRef.current &&
                !buttonRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            const updatePosition = () => {
                if (buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    setCoords({
                        top: rect.bottom + window.scrollY + 4,
                        left: rect.left + window.scrollX
                    });
                }
            };

            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', () => setIsOpen(false), true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', () => setIsOpen(false), true);
            };
        }
    }, [isOpen]);

    return (
        <>
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 border transition-all ${statusColors[currentStatus] || 'bg-gray-100 border-gray-200'}`}
            >
                <span className="whitespace-nowrap">{currentStatus}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: coords.top,
                        left: coords.left,
                        zIndex: 9999,
                        minWidth: '140px'
                    }}
                    className="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden animate-fade-in-up"
                >
                    {['Pending', 'Interviewing', 'Rejected', 'Hired'].map((status) => (
                        <button
                            key={status}
                            onClick={() => {
                                onStatusChange(status);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between text-xs transition-colors ${currentStatus === status ? 'text-blue-600 bg-blue-50/50 font-medium' : 'text-gray-700'
                                }`}
                        >
                            <span>{status}</span>
                            {currentStatus === status && <Check className="w-3 h-3" />}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:scale-[1.02]">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6" />
        </div>
        <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
    </div>
);

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className={`p-1 rounded transition-all ${copied
                ? 'text-green-600 bg-green-50'
                : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100'
                }`}
            title={copied ? "Copied!" : "Copy Phone Number"}
        >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
    );
};

export default Careers;
